import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, Ban, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { AdminTabs } from "../../components/admin/AdminTabs";
import { useConfirm } from "../../components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const PLAN_STYLE = {
  free: "bg-stone-bg text-stone-sub", trial: "bg-brand-soft text-brand",
  basic: "bg-housing/15 text-housing", advanced: "bg-stone-text/10 text-stone-text",
};

export default function AdminUsers() {
  const { t, i18n } = useTranslation();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("all");
  const [fict, setFict] = useState("all");
  const [counts, setCounts] = useState({});
  const [appsModal, setAppsModal] = useState(null); // {user, apps, loading}

  const load = () => {
    api.get("/admin/users").then(({ data }) => setUsers(data || [])).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/application-counts").then(({ data }) => setCounts(data || {})).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openApps = async (u) => {
    setAppsModal({ user: u, apps: [], loading: true });
    try {
      const { data } = await api.get(`/admin/users/${u.id}/applications`);
      setAppsModal({ user: u, apps: data || [], loading: false });
    } catch (err) {
      setAppsModal({ user: u, apps: [], loading: false });
    }
  };

  const filtered = useMemo(() => {
    return users.filter((u) => {
      if (role !== "all" && u.role !== role) return false;
      if (fict === "real" && u.is_fictitious === true) return false;
      if (fict === "fictitious" && u.is_fictitious !== true) return false;
      if (q.trim()) { const s = q.toLowerCase(); return u.name?.toLowerCase().includes(s) || u.email?.toLowerCase().includes(s); }
      return true;
    });
  }, [users, q, role, fict]);

  const toggleActive = async (u) => {
    if (u.active && !(await confirm({
      title: t("admin.users.suspend"),
      description: t("admin.users.suspendConfirm", { name: u.name }),
      confirmText: t("confirm.continue"),
    }))) return;
    try {
      const { data } = await api.put(`/admin/users/${u.id}/toggle`);
      const active = typeof data?.active === "boolean" ? data.active : !u.active;
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, active } : x)));
      toast.success(t("admin.users.updated"));
    } catch (err) { toast.error(t("common.error")); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString(i18n.language) : "");
  const roles = ["all", "candidate", "employer", "admin"];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("admin.users.title")}</h1>
      <AdminTabs />

      <div className="flex gap-3 mb-5 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-muted" />
          <input data-testid="users-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("admin.users.search")} className="w-full pl-12 pr-4 py-3 rounded-xl border border-stone-border bg-white focus:outline-none focus:ring-2 focus:ring-brand" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {roles.map((r) => (
            <button key={r} data-testid={`users-filter-${r}`} onClick={() => setRole(r)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${role === r ? "bg-brand text-white border-brand" : "bg-white text-stone-sub border-stone-border hover:border-brand"}`}>
              {t(`admin.users.${r === "all" ? "all" : r}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 flex-wrap mb-5" data-testid="users-fict-filter">
        {["all", "real", "fictitious"].map((f) => (
          <button key={f} data-testid={`users-fict-${f}`} onClick={() => setFict(f)} className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${fict === f ? "bg-brand text-white border-brand" : "bg-white text-stone-sub border-stone-border hover:border-brand"}`}>
            {t(`admin.users.fict_${f}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : (
        <div className="bg-white rounded-2xl border border-stone-border shadow-sm overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-stone-muted border-b border-stone-border">
                <th className="p-4 font-medium">{t("admin.users.plan")}</th>
                <th className="p-4 font-medium">{t("admin.users.type")}</th>
                <th className="p-4 font-medium hidden sm:table-cell">{t("admin.users.registered")}</th>
                <th className="p-4 font-medium hidden md:table-cell">{t("admin.users.referrals")}</th>
                <th className="p-4 font-medium hidden lg:table-cell">{t("admin.users.applications")}</th>
                <th className="p-4 font-medium">{t("admin.users.status")}</th>
                <th className="p-4 font-medium text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((u) => (
                <tr key={u.id} className="border-b border-stone-border/60 last:border-0" data-testid="user-row">
                  <td className="p-4">
                    <div className="font-heading font-semibold text-stone-text">{u.name}</div>
                    <div className="text-stone-muted text-xs">{u.email}</div>
                    <div className="flex gap-1.5 mt-1">
                      <span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-stone-bg text-stone-sub">{u.role}</span>
                      <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full ${PLAN_STYLE[u.subscription?.plan] || "bg-stone-bg text-stone-sub"}`}>{u.subscription?.plan || "free"}</span>
                    </div>
                  </td>
                  <td className="p-4">
                    {u.is_fictitious === true ? (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-red-100 text-red-600" data-testid="user-fict-badge">{t("admin.users.fictitious")}</span>
                    ) : (
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-housing/15 text-housing" data-testid="user-real-badge">{t("admin.users.real")}</span>
                    )}
                  </td>
                  <td className="p-4 text-stone-sub hidden sm:table-cell">{fmt(u.created_at)}</td>
                  <td className="p-4 text-stone-sub hidden md:table-cell">{u.referral_count ?? 0}</td>
                  <td className="p-4 hidden lg:table-cell">
                    {u.role === "candidate" ? (
                      <button data-testid="user-apps-btn" onClick={() => openApps(u)} className="inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
                        <FileText className="w-3.5 h-3.5" /> {counts[u.id] ?? 0}
                      </button>
                    ) : <span className="text-stone-muted">—</span>}
                  </td>
                  <td className="p-4">
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${u.active ? "bg-housing/15 text-housing" : "bg-red-100 text-red-600"}`}>
                      {u.active ? t("admin.users.active") : t("admin.users.suspended")}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    {u.role !== "admin" && (
                      <button data-testid="user-toggle-btn" onClick={() => toggleActive(u)} className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${u.active ? "text-red-600 hover:bg-red-50" : "text-housing hover:bg-housing/10"}`}>
                        {u.active ? <><Ban className="w-3.5 h-3.5" /> {t("admin.users.suspend")}</> : <><CheckCircle2 className="w-3.5 h-3.5" /> {t("admin.users.activate")}</>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!appsModal} onOpenChange={(o) => !o && setAppsModal(null)}>
        <DialogContent data-testid="user-apps-modal" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("admin.users.appsOf", { name: appsModal?.user?.name || "" })}</DialogTitle>
            <DialogDescription>{appsModal?.user?.email}</DialogDescription>
          </DialogHeader>
          {appsModal?.loading ? (
            <div className="py-8 text-center text-stone-muted">{t("jobs.loading")}</div>
          ) : (appsModal?.apps || []).length === 0 ? (
            <div className="py-8 text-center text-stone-muted" data-testid="user-apps-empty">{t("admin.users.noApps")}</div>
          ) : (
            <div className="space-y-2" data-testid="user-apps-list">
              {appsModal.apps.map((a) => (
                <div key={a.id} className="bg-stone-bg rounded-xl p-3">
                  <div className="font-heading font-semibold text-stone-text">{a.job?.title || "—"}</div>
                  <div className="text-sm text-stone-sub">{a.job?.restaurant?.name} · {a.job?.city}{a.job?.state ? `, ${a.job.state}` : ""}</div>
                  {a.message && <div className="text-sm text-stone-muted mt-1">{a.message}</div>}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
